import { useEffect, useState } from "react";
import { CheckCircle2, Circle, AlertCircle, Database, ExternalLink, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PLATFORM_LABELS = {
  muso_ai_claimed: "Muso.AI · Claimed",
  muso_ai_verified: "Muso.AI · Verified",
  allmusic_profile: "AllMusic",
  musicbrainz_listed: "MusicBrainz",
  genius_listed: "Genius",
  discogs_listed: "Discogs",
  jaxsta_listed: "Jaxsta",
  wikidata_listed: "Wikidata",
  imdb_listed: "IMDb",
  official_credits_page: "Official Credits Page",
  monitor_public_credits: "Credit Monitoring",
};

function statusIcon(credit) {
  if (credit >= 1.0) return <CheckCircle2 size={14} className="text-emerald-400" />;
  if (credit >= 0.4) return <AlertCircle size={14} className="text-amber-500" />;
  return <Circle size={14} className="text-neutral-600" />;
}

export default function MetadataAuthorityCard({ assessment, onUpdate }) {
  const [links, setLinks] = useState({});
  const [pending, setPending] = useState(null); // question_id of the open confirm tray
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/assessment/connect-links"); setLinks(data.links || {}); }
      catch { /* noop */ }
    })();
  }, []);

  if (!assessment) return null;
  const sub = assessment.sub_scores?.metadata_authority;
  const platforms = assessment.metadata_platforms || [];
  if (!sub) return null;
  const color = sub.status?.color || (sub.zone === "green" ? "#10B981" : sub.zone === "yellow" ? "#F59E0B" : "#E11D48");
  const claimed = platforms.filter((p) => p.credit >= 0.4).length;
  const verified = platforms.filter((p) => p.credit >= 1.0).length;

  const openConnect = (qid) => {
    const url = links[qid];
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setPending(qid);
  };

  const markConnected = async (qid, value = "Yes") => {
    setBusy(true);
    try {
      const { data } = await api.post("/assessment/connect-platform", { question_id: qid, value });
      toast.success(`${PLATFORM_LABELS[qid] || qid} marked as connected. Score: ${data.sub_scores.metadata_authority.score}/1000`);
      setPending(null);
      onUpdate?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="metadata-authority-card" className="bg-[#0c0c0c] p-8 lg:col-span-12 border-t border-white/5">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-amber-500" />
            <div className="text-overline">Metadata &amp; Credit Authority</div>
          </div>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="font-display text-5xl lg:text-6xl font-medium" style={{ color }} data-testid="metadata-authority-score">
              {sub.score}
            </span>
            <span className="font-mono-tech text-xs text-neutral-500">/ 1000</span>
          </div>
          <div className="text-overline mb-3" style={{ color }}>{sub.status?.label || sub.risk_rating}</div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            One-click connects below. Knock them out and watch this score climb in real time.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-px bg-white/5">
            <div className="bg-[#0A0A0A] p-3">
              <div className="text-overline mb-1">Verified</div>
              <div className="font-display text-2xl text-emerald-400">{verified}</div>
            </div>
            <div className="bg-[#0A0A0A] p-3">
              <div className="text-overline mb-1">Claimed+</div>
              <div className="font-display text-2xl text-amber-500">{claimed}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <div className="text-overline">Credit Authority Checks</div>
            <div className="font-mono-tech text-xs text-neutral-500">Click Connect → confirm on return</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {platforms.map((p) => {
              const url = links[p.id];
              const isYes = p.credit >= 1.0;
              const isPending = pending === p.id;
              return (
                <div key={p.id} data-testid={`metadata-platform-${p.id}`}
                  className={`px-4 py-3 border bg-[#0A0A0A] transition-colors
                    ${isYes ? "border-emerald-500/30" : isPending ? "border-amber-500/50" : "border-white/5 hover:border-white/15"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {statusIcon(p.credit)}
                      <span className="text-sm text-white truncate">{PLATFORM_LABELS[p.id] || p.id}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs uppercase tracking-widest font-semibold
                        ${p.credit >= 1.0 ? "text-emerald-400" : p.credit >= 0.4 ? "text-amber-500" : "text-neutral-500"}`}>
                        {p.answer}
                      </span>
                      {!isYes && (
                        url ? (
                          <button data-testid={`connect-${p.id}`} onClick={() => openConnect(p.id)}
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-2 py-1 border border-amber-500/40 text-amber-500 hover:bg-amber-500/10 transition-colors">
                            Connect <ExternalLink size={10} />
                          </button>
                        ) : (
                          <button data-testid={`mark-done-${p.id}`} onClick={() => markConnected(p.id, "Yes")}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-2 py-1 border border-amber-500/40 text-amber-500 hover:bg-amber-500/10 transition-colors">
                            Mark Done
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {isPending && (
                    <div className="mt-3 pt-3 border-t border-amber-500/20 flex items-center gap-2" data-testid={`confirm-tray-${p.id}`}>
                      <span className="text-xs text-neutral-300 flex-1">Did you complete it on {PLATFORM_LABELS[p.id]?.split(" · ")[0] || p.id}?</span>
                      <button onClick={() => markConnected(p.id, "Yes")} disabled={busy}
                        data-testid={`confirm-yes-${p.id}`}
                        className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 bg-amber-500 text-black hover:brightness-110 transition-all disabled:opacity-50 inline-flex items-center gap-1">
                        {busy ? <Loader2 size={10} className="animate-spin" /> : null} Mark Connected
                      </button>
                      <button onClick={() => setPending(null)} disabled={busy}
                        data-testid={`confirm-cancel-${p.id}`}
                        className="text-neutral-400 hover:text-white p-1">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
